import {
  CriminalRecordType,
  NodeRefFullInput,
  NodeType,
  TodoPriority,
  UserFragment
} from "@graphql";
import { observable } from "mobx";
import { IsNotEmpty } from "class-validator";
import locale from "@bonliva-crm/locale";
import { ConsultantEducationType, ConsultantSITHSErrand } from "../utils";

export type ConsultantData = { Name?: string; _id?: string };

export type ReferenceData = {
  _id: string | null;
  name: string;
  phone: string;
  formType: string;
  email: string;
  position?: string;
  relation?: string;
};

export class CreateTicketData {
  @observable
  @IsNotEmpty({ message: locale.tickets.textRequired })
  title: string = "";

  @observable
  description: string = "";

  @observable
  errand: ConsultantSITHSErrand | null = null;

  @observable
  deliveryPlace: string | null = null;

  @observable
  dueDate: Date | null = null;

  @observable
  assignedUser: UserFragment | null = null;

  @observable
  assignedTeam: string | null = null;

  @observable
  tags: string[] = [];

  @observable
  linkedItems: NodeRefFullInput[] = [];

  @observable
  priority: TodoPriority | null = null;

  @observable
  priorityHelperText: string | null = null;

  @observable
  dueDateText: string | null = null;

  @observable
  dueDateDisabled: true | false = false;

  @observable
  criminalRecordType: CriminalRecordType | null = null;

  @observable
  reference: ReferenceData = {
    _id: null,
    name: "",
    phone: "",
    formType: "",
    position: "",
    relation: "",
    email: ""
  };

  @observable
  educationType: ConsultantEducationType | null = null;

  @observable
  chargedProjects: string | null = null;

  @observable
  perfPlace: string | null = null;

  @observable
  workInStockholm: "true" | "false" | null = null;

  static Empty() {
    return new CreateTicketData();
  }

  private commonData(consultant: ConsultantData) {
    this.assignedTeam = "QualityDepartment";
    this.priority = TodoPriority.NoStatus;
    this.criminalRecordType = null as CriminalRecordType;
    this.description = "";
    this.linkedItems.push({
      Name: consultant.Name,
      NodeID: consultant._id,
      NodeType: NodeType.Consultant
    });
  }

  private handleBelastningsregister() {
    this.title = "Nytt belastningsregister";
    this.dueDateText = "Läggs på posten samma dag, om beställning sker innan kl 16.";
  }

  private handleSAK() {
    this.title = "SAK behöver uppdateras";
    this.tags = ["SAK"];
  }

  private handleIvoUppd() {
    this.title = "IVO behöver uppdateras";
    this.tags = ["IVO"];
    this.dueDateText =
      "Handläggningstid kan variera mellan 1-5 dagar. Finns specifik dag för deadline, fyll i förfallodatum samt skriv detta under ”Övrig info”.";
    this.priorityHelperText =
      "Använd enbart hög prioritet om IVO behöver komma in samma dag som beställningen görs.";
  }

  private handleIvoGrund() {
    this.title = "Beställ IVO-grund";
    this.tags = ["IVO"];
    this.dueDateText =
      "Handläggningstid kan variera mellan 1-5 dagar. Finns specifik dag för deadline, fyll i förfallodatum samt skriv detta under ”Övrig info”.";
    this.priorityHelperText =
      "Använd enbart hög prioritet om IVO behöver komma in samma dag som beställningen görs.";
  }

  private handleReferens(reference: ReferenceData | null, update: boolean) {
    this.title = `Hjälp med ${update ? "uppdatering av" : null} ${reference.formType} (${
      reference.name
    })`.trim();
    this.tags = ["Referens"];

    if (reference) {
      this.reference._id = reference._id;
      this.reference.email = reference.email;
      this.reference.name = reference.name;
      this.reference.phone = reference.phone;
      this.reference.relation = reference?.relation || "";
      this.reference.position = reference?.position || "";
    }

    this.dueDateText =
      "Ordinarie handläggningstid är 4-5 arbetsdagar, om det avviker så hör vi av oss";
    this.priorityHelperText =
      "Använd bara om du behöver ref inom 1-2 arbetsdagar. Om du väljer hög prioritet påbörjas referens så snart som möjligt.";
  }

  private handleHPR() {
    this.title = "HPR behöver uppdateras";
    this.tags = ["Norge HPR (intresseanmälan)"];
  }

  private handleSITHS() {
    this.title = "HSAID/SITHS";
    this.errand = ConsultantSITHSErrand.OrderCard;
    this.deliveryPlace = "";
    this.tags = ["Siths-kort"];
  }

  private handleHLR(name: string) {
    this.title = `Beställning av ${name}`;
    this.perfPlace = "";
    this.chargedProjects = "";
    this.educationType = null;
  }

  private handleCriminalRecord(name: string) {
    this.title = `Nytt ${name.toLowerCase()}`;
    this.tags = ["Belastningsregister"];
    this.perfPlace = "";
    this.chargedProjects = "";
  }

  static ByReferenceType(
    consultant: ConsultantData,
    referenceData: ReferenceData,
    update = false
  ) {
    const data = new CreateTicketData();
    data.commonData(consultant);
    data.handleReferens(referenceData, update);

    return data;
  }

  static ByQualificationName(consultant: ConsultantData, name: string) {
    const data = new CreateTicketData();
    data.commonData(consultant);

    if (name == "Helsetillsynet (SAK)") {
      data.handleSAK();
    }
    if (name == "Belastningsregister") {
      data.handleBelastningsregister();
    }
    if (name == "HPR-nummer") {
      data.handleHPR();
    }
    if (name == "IVO-Uppdatering") {
      data.handleIvoUppd();
    }
    if (name == "IVO-Grund") {
      data.handleIvoGrund();
    }
    if (name == "HSAID/SITHS") {
      data.handleSITHS();
    }

    if (
      ["HLR", "S-HLR Barn", "S-HLR Vuxen", "A-HLR Vuxen", "A-HLR Barn"].includes(name)
    ) {
      data.handleHLR(name);
    }

    if (["Belastningsregister", "Belastningsregister Nationella"].includes(name)) {
      data.handleCriminalRecord(name);
    }

    return data;
  }
}
